import { usePresentationController } from '@/stores/presentationController'
import { onKeyStroke, useSwipe } from '@vueuse/core'
import { Swiper } from 'swiper'
import { computed, onMounted, type Ref, ref, watch } from 'vue'

/*
 * localNavigation
 * This allows a slide to prevent the parent swiper instance from swiping away
 * from a slide that has local navigation it needs to have done.
 * Parameters
 * parentSwiper - this is provided as a prop by the presentation
 * parentDiv - needed to determine swiping also provided as a prop
 * onNext - a callback given to tell the consuming component when to go next
 * onPrev - a callback given to tell the consuming component when to go back
 *
 * returned
 * changeAllowNext & changeAllowPrev - receives a boolean so the consuming
 *    component can set weather we can be allowed to navigate before and after
 **/
export function localNavigation(opts: {
	slideId?: string
	swipingElRef?: Ref<HTMLElement>
	parentSwiper?: Swiper
	onNext: () => void
	onPrev: () => void
}) {
	const { slideId, swipingElRef, parentSwiper, onNext, onPrev } = opts

	const controller = usePresentationController()
	const activeSlide = computed(() => controller.activeSlideId === slideId)

	/**
	 * Track if we are the active slide or not.
	 * We only update it after the epoch because we get keyboard events
	 * after swiper already moved us to the next slide
	 */
	const delayedActiveSlide = ref(false)
	watch(activeSlide, (value) => {
		setTimeout(() => {
			delayedActiveSlide.value = value
		}, 0)
	})

	if (swipingElRef) {
		useSwipe(swipingElRef, {
			onSwipeEnd(e, direction) {
				// We only want to allow this if we are on the correct slide
				if (!activeSlide.value) return
				if (direction === 'left') onNext()
				if (direction === 'right') onPrev()
			},
		})
	}

	/*
	 * Only trigger keyboard navigation if we were on this slide
	 * when the user pressed the key
	 */
	onKeyStroke(['ArrowLeft', 'ArrowRight'], ({ code }) => {
		// We only want to allow this if we are on the correct slide
		if (!delayedActiveSlide.value) return
		if (code === 'ArrowRight') onNext()
		if (code === 'ArrowLeft') onPrev()
	})

	onMounted(() => {
		if (parentSwiper && slideId) {
			controller.setupLocalSlides(slideId, parentSwiper)
			delayedActiveSlide.value = activeSlide.value
		}
	})

	function changeAllowNext(allowNext: boolean) {
		// We only want to allow this if we are on the correct slide
		if (!parentSwiper || !activeSlide) return
		parentSwiper.allowSlideNext = allowNext
	}

	function changeAllowPrev(allowPrev: boolean) {
		// We only want to allow this if we are on the correct slide
		if (!parentSwiper || !activeSlide) return
		parentSwiper.allowSlidePrev = allowPrev
	}

	return { changeAllowNext, changeAllowPrev }
}
